'use client';

import { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface NavigationItem {
  label: string;
  href?: string;
  children?: NavigationItem[];
}

interface HeaderProps {
  className?: string;
}

const Header = ({ className = '' }: HeaderProps) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isDark, setIsDark] = useState(false);
  const headerRef = useRef<HTMLElement | null>(null);
  const [scrolled, setScrolled] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);

  const navigationItems: NavigationItem[] = [
    { label: 'Home', href: '/homepage' },
    { label: 'Leadership Team', href: '/leadership-team' },
    {
      label: 'Case Studies',
      children: [
        { label: 'Batis Poultry', href: '/dataroom/batispoultry' },
        { label: 'Pixies', href: '/dataroom/pixies' },
      ],
    },
    { label: 'Transformation Agenda', href: '/the-transformation-agenda' },
    { label: 'Investment Approach', href: '/investment-approach' },
    { label: 'Contact Us', href: '/regional-operations' },
    // { label: 'Impact Framework', href: '/impact-framework' },
  ];

  const ctaItem: NavigationItem = {
    label: 'Partnership Portal',
    href: '/partnership-portal',
  };

  useEffect(() => {
  const headerEl = headerRef.current;
  if (!headerEl) return;

  const headerHeight = headerEl.offsetHeight;

  // ── Scroll handler ──
  const handleScroll = () => {
    setScrolled(window.scrollY > 60);
  };

  window.addEventListener('scroll', handleScroll, { passive: true });

  // ── Intersection observer ──
  const sections = Array.from(
    document.querySelectorAll<HTMLElement>('[data-header="dark"]')
  );

  let observer: IntersectionObserver | null = null;

  if (sections.length) {
    observer = new IntersectionObserver(
      (entries) => {
        let shouldBeDark = false;

        for (const entry of entries) {
          const rect = entry.boundingClientRect;

          if (rect.top <= headerHeight && rect.bottom > headerHeight) {
            shouldBeDark = true;
            break;
          }
        }

        setIsDark(shouldBeDark);
      },
      {
        root: null,
        threshold: Array.from({ length: 101 }, (_, i) => i / 100),
      }
    );

    sections.forEach((section) => observer!.observe(section));
  }

  // ── Cleanup ──
  return () => {
    window.removeEventListener('scroll', handleScroll);
    observer?.disconnect();
  };
}, []);
  return (
    <header
      ref={headerRef}
      className={`fixed top-0 left-0 w-full z-50 transition-all backdrop-blur-xs  duration-500 ${
        scrolled ? 'nav-scrolled py-1' : 'py-3'
      }`}
    >
      <div className="w-full  mx-auto px-4">
        <div className="flex items-center justify-between h-24">
          {/* Logo */}
          <Link href="/homepage" className="flex items-center space-x-3">
            <div className="w-20 h-auto">
              <img
                src="/assets/images/logo.png"
                alt="Grandville Holdings Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div className="flex flex-col">
              <span className="font-headline text-xl text-yellow-400  font-semibold">
                Grandville Holdings
              </span>
              <span className={`font-body text-sm text-yellow-400 `}>Building What Lasts</span>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navigationItems.map((item) =>
              item.children ? (
                <div key={item.label} className="relative group">
                  <button
                    className={`px-4 py-2 text-sm rounded-md transition flex items-center gap-1 text-white/70`}
                  >
                    {item.label}
                  </button>

                  <div
  className="
    absolute left-0 mt-2 w-56 rounded-md shadow-lg bg-white/95 backdrop-blur
    opacity-0 invisible translate-y-2
    group-hover:opacity-100 group-hover:visible group-hover:translate-y-0
    transition-all duration-200 ease-out
    delay-75
  "
>
                    {item.children.map((sub) => (
                      <Link
                        key={sub.href}
                        href={sub.href!}
                        className="block px-4 py-2 text-sm hover:bg-muted"
                      >
                        {sub.label}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={item.href}
                  href={item.href!}
                  className={`px-4 py-2 text-sm rounded-md transition  text-white/70`}
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>

          {/* Mobile toggle */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`lg:hidden p-2 rounded-md
             text-white hover:bg-white/10`}
          >
            <Icon name={isMobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} variant="outline" />
          </button>
        </div>
      </div>
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-card border-t border-border animate-slide-down">
          {' '}
          <nav className="container mx-auto px-4 py-4 space-y-1">
            {navigationItems.map((item) =>
              item.children ? (
                <div key={item.label}>
                  <button
                    onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                    className="w-full flex items-center justify-between px-4 py-3 text-base font-medium text-text-primary hover:bg-muted rounded-md"
                  >
                    {item.label}
                    <Icon
                      name={openDropdown === item.label ? 'ChevronUpIcon' : 'ChevronDownIcon'}
                      size={18}
                    />
                  </button>

                  {openDropdown === item.label && (
                    <div className="ml-4 mt-1 space-y-1">
                      {item.children.map((sub) => (
                        <Link
                          key={sub.href}
                          href={sub.href!}
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            setOpenDropdown(null);
                          }}
                          className="block px-4 py-2 text-sm text-text-primary hover:bg-muted rounded-md"
                        >
                          {sub.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.href}
                  href={item.href!}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="block px-4 py-3 text-base font-body text-text-primary hover:bg-muted rounded-md"
                >
                  {item.label}
                </Link>
              )
            )}
          </nav>{' '}
        </div>
      )}
    </header>
  );
};

export default Header;
